import { FunctionComponent, ReactElement, useMemo, useState } from 'react';
import { Customer, DeviceStatistics, SingleSite } from '@/core/entities';
import { ALL_SITES_ID, useMostActiveDevices } from '@/queries/logs';
import { Column } from '@/components/ui/table/types';
import { MostActiveDeviceResponse } from '@/api/dashboard/types';
import { Typography } from '@mui/material';
import StatisticsTable from './StatisticsTable';
import { QueryDateRange } from '../../types';
import { formatNumber, getPercent, getPercentColor } from '../../utils';
import { SelectedLocation } from '../completed-cycles/filters/LocationFiltersDialog';

const MostActiveDevices: FunctionComponent<{
	site?: SingleSite;
	locationsFilter?: SelectedLocation[];
	locationId?: string;
	customer?: Pick<Customer, 'id' | 'name'>;
	showAllSitesData: boolean;
	isAdmin: boolean;
	dateRange: QueryDateRange;
}> = ({ site, locationId, customer, showAllSitesData, isAdmin, dateRange }): ReactElement => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const params: Record<string, any> = {
		fromDate: dateRange.start,
		toDate: dateRange.end,
	};

	if (customer) {
		params.customerId = customer.id;
	}

	if (locationId) {
		params.locationId = locationId;
	}
	const { query: mostActiveDevicesQuery } = useMostActiveDevices(
		params,
		showAllSitesData ? ALL_SITES_ID : site?.id
	);

	const isLoading = useMemo(
		() => mostActiveDevicesQuery.isFetching,
		[mostActiveDevicesQuery.isFetching]
	);

	const [viewMode, setViewMode] = useState<'completed' | 'error'>('completed');

	const tableColumns = useMemo(() => {
		const columns: Column<MostActiveDeviceResponse>[] = [
			{
				name: 'Device',
				render: ({ customer_name, site_name, device_name }) => (
					<div>
						<div>{device_name}</div>
						{isAdmin ? <small>{site_name || customer_name}</small> : <small>{site_name}</small>}
					</div>
				),
				accessors: ['customer_name', 'site_name', 'device_name'],
			},
			{
				name: 'Cycles',
				accessors: ['count'],
				align: 'right',
				render: ({ count }) => formatNumber(count),
			},
			{
				name: 'Completed',
				accessors: ['complete_count'],
				align: 'right',
				render: ({ complete_count }) => formatNumber(complete_count),
			},
			{
				name: '%',
				render: ({ count = 0, complete_count = 0 }) => {
					const completionPercent = getPercent(count, complete_count);
					const errorRate = 100 - completionPercent;
					const color = getPercentColor(viewMode === 'error' ? errorRate : completionPercent, viewMode === 'error');
					return <Typography sx={{ color }}>{formatNumber(completionPercent)}%</Typography>;
				},
				accessors: ['count', 'complete_count'],
				align: 'right',
				minWidth: 80,
			},
		];
		return columns;
	}, [isAdmin, viewMode]);

	const dataTransform = (items: MostActiveDeviceResponse[]) => {
		if (viewMode === 'completed') return items;
		return [...items].sort((a, b) => {
			const errorRateA = 100 - getPercent(a.count, a.complete_count);
			const errorRateB = 100 - getPercent(b.count, b.complete_count);
			return errorRateB - errorRateA;
		});
	};

	const sx = {
		'@media print': {
			margin: '20px',
			boxShadow: 'none',
			pageBreakInside: 'avoid',
			breakInside: 'avoid',
		},
	};

	return (
		<StatisticsTable<DeviceStatistics>
			title="Devices"
			query={mostActiveDevicesQuery}
			columns={tableColumns}
			emptyState="No devices available"
			dataTransform={dataTransform}
			showViewButtons
			onViewChange={setViewMode}
			viewMode={viewMode}
			sx={sx}
			isLoading={isLoading}
		/>
	);
};

export default MostActiveDevices;
